import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft, UserCheck, UserPlus, ExternalLink, Tv, Film, Radio, Award,
  Sparkles, Loader2, Crown, Heart, Share2, Copy, Check as CheckIcon,
} from "lucide-react";
import { toast } from "sonner";
import { api, API_BASE } from "../lib/api";
import { useAuth } from "../lib/auth";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

function RoleBadge({ label, color = "violet", Icon }) {
  const palette = {
    red: "bg-[#E50914]/15 border-[#E50914]/30 text-[#E50914]",
    gold: "bg-[#f6c34a]/15 border-[#f6c34a]/40 text-[#f6c34a]",
    cyan: "bg-cyan-500/15 border-cyan-500/30 text-cyan-300",
    violet: "bg-violet-500/15 border-violet-500/40 text-violet-300",
    amber: "bg-amber-500/15 border-amber-500/30 text-amber-400",
  };
  return (
    <span className={`px-2.5 py-1 text-[10px] uppercase tracking-widest font-bold rounded border inline-flex items-center gap-1 ${palette[color]}`}>
      {Icon && <Icon className="w-3 h-3" />} {label}
    </span>
  );
}

export default function CreatorProfile() {
  const { userId } = useParams();
  const { user: viewer } = useAuth();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const shareUrl = typeof window !== "undefined"
    ? `${window.location.origin}/creator/${userId}` : "";
  const ogUrl = `${API_BASE}/creators/${userId}/og.png`;

  const copyShare = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Share link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy — long-press the link to copy manually");
    }
  };

  useEffect(() => {
    let cancelled = false;
    api.get(`/creators/${userId}`)
      .then((r) => !cancelled && setP(r.data))
      .catch((e) => !cancelled && setErr(e?.response?.data?.detail || "Profile not found"));
    return () => { cancelled = true; };
  }, [userId]);

  const toggleFollow = async () => {
    if (!viewer) { toast.error("Sign in to follow creators"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/creators/${userId}/follow`);
      setP((prev) => prev && ({ ...prev, is_following: data.action === "followed", follower_count: data.follower_count }));
      toast.success(data.action === "followed" ? "Following — you'll get push alerts on new releases" : "Unfollowed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not toggle follow");
    } finally { setBusy(false); }
  };

  if (err) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white">
        <Navbar />
        <div className="pt-32 max-w-xl mx-auto px-6 text-center">
          <h1 className="text-3xl font-black mb-3">Creator not found</h1>
          <p className="text-gray-400 mb-6">{err}</p>
          <Link to="/browse" className="btn-red px-6 py-2.5 rounded inline-block">Back to Browse</Link>
        </div>
        <Footer />
      </div>
    );
  }
  if (!p) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white">
        <Navbar />
        <div className="pt-32 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#E50914]" /></div>
      </div>
    );
  }

  const initials = (p.name || "?").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Navbar />
      <main className="pt-24 pb-20" data-testid="creator-profile-page">
        {/* Hero header */}
        <section className="relative px-6 lg:px-12 pt-12 pb-10 border-b border-white/5 overflow-hidden">
          <div
            aria-hidden="true"
            className="absolute inset-0 opacity-[0.06] pointer-events-none"
            style={{
              backgroundImage:
                "radial-gradient(circle at 18% 30%, #E50914 0%, transparent 45%), radial-gradient(circle at 82% 70%, #f6c34a 0%, transparent 45%)",
            }}
          />
          <div className="relative max-w-[1600px] mx-auto flex items-start gap-6 flex-wrap">
            {/* Avatar */}
            <div className="shrink-0">
              {p.picture ? (
                <img src={p.picture} alt={p.name} className="w-28 h-28 rounded-full object-cover border-4 border-[#f6c34a]/30" />
              ) : (
                <div className="w-28 h-28 rounded-full bg-gradient-to-br from-[#E50914] to-[#7a050a] border-4 border-[#f6c34a]/30 flex items-center justify-center text-3xl font-black"
                     data-testid="creator-avatar-fallback">
                  {initials}
                </div>
              )}
            </div>

            <div className="flex-1 min-w-0">
              <div className="text-xs uppercase tracking-[0.3em] text-[#f6c34a] mb-2 flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5" /> Creator profile
              </div>
              <h1 className="text-4xl md:text-5xl font-black tracking-tight" data-testid="creator-name">
                {p.name}
              </h1>
              <div className="flex flex-wrap gap-2 mt-3">
                {p.is_admin && <RoleBadge label="Admin" color="red" Icon={Crown} />}
                {p.is_filmmaker && <RoleBadge label="Filmmaker" color="amber" Icon={Award} />}
                {p.is_channel_owner && <RoleBadge label="Channel Owner" color="cyan" Icon={Radio} />}
                {p.is_content_owner && <RoleBadge label="Content Owner" color="violet" Icon={Film} />}
              </div>
              {p.bio && <p className="text-gray-300 mt-5 max-w-2xl leading-relaxed">{p.bio}</p>}
              {p.portfolio_url && (
                <a
                  href={p.portfolio_url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="creator-portfolio-link"
                  className="text-[#f6c34a] hover:text-white text-sm mt-3 inline-flex items-center gap-1"
                >
                  <ExternalLink className="w-3.5 h-3.5" /> {p.portfolio_url}
                </a>
              )}

              <div className="flex items-center gap-4 mt-6 flex-wrap">
                <div className="flex items-center gap-2 text-sm">
                  <Heart className="w-4 h-4 text-[#E50914]" />
                  <span data-testid="creator-followers" className="tabular-nums font-bold">{p.follower_count.toLocaleString()}</span>
                  <span className="text-gray-500">follower{p.follower_count === 1 ? "" : "s"}</span>
                </div>
                {!p.is_self ? (
                  <button
                    data-testid="creator-follow-btn"
                    onClick={toggleFollow}
                    disabled={busy}
                    className={`px-5 py-2 rounded font-semibold text-sm transition flex items-center gap-2 ${
                      p.is_following
                        ? "bg-white/10 border border-white/20 hover:bg-white/15 text-white"
                        : "bg-[#E50914] hover:bg-[#b00710] text-white"
                    }`}
                  >
                    {p.is_following ? <><UserCheck className="w-4 h-4" /> Following</> : <><UserPlus className="w-4 h-4" /> Follow</>}
                  </button>
                ) : (
                  <Link
                    to="/dashboard"
                    data-testid="creator-self-dashboard"
                    className="text-xs text-[#f6c34a] hover:text-white underline"
                  >
                    This is you — open your dashboard →
                  </Link>
                )}
                <button
                  data-testid="creator-share-btn"
                  onClick={() => setShareOpen(true)}
                  className="px-4 py-2 rounded font-semibold text-sm bg-white/5 border border-white/10 hover:bg-white/10 text-white transition flex items-center gap-2"
                  title="Share this profile"
                >
                  <Share2 className="w-4 h-4" /> Share
                </button>
              </div>
            </div>
          </div>
        </section>

        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 mt-10 space-y-12">
          {/* Channels */}
          {p.channels.length > 0 && (
            <section data-testid="creator-channels-section">
              <h2 className="text-xl md:text-2xl font-bold mb-4 flex items-center gap-2">
                <Radio className="w-5 h-5 text-cyan-400" /> Channels
                <span className="text-xs text-gray-500 font-normal">· {p.channels.length}</span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {p.channels.map((c) => (
                  <Link
                    key={c.channel_id}
                    to={`/live/${c.slug}`}
                    className="block rounded-xl border border-white/10 bg-[#0e0e0e] hover:border-cyan-500/40 transition p-5"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg flex items-center justify-center"
                           style={{ background: `${c.color || "#22d3ee"}22`, color: c.color || "#22d3ee" }}>
                        <Tv className="w-6 h-6" />
                      </div>
                      <div className="min-w-0">
                        <div className="font-bold truncate">{c.name}</div>
                        <div className="text-xs text-gray-500 truncate">{c.description || "Live broadcast channel"}</div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* Festival submissions */}
          {p.submissions.length > 0 && (
            <section data-testid="creator-submissions-section">
              <h2 className="text-xl md:text-2xl font-bold mb-4 flex items-center gap-2">
                <Award className="w-5 h-5 text-amber-400" /> Festival films
                <span className="text-xs text-gray-500 font-normal">· {p.submissions.length}</span>
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {p.submissions.map((s) => (
                  <Link
                    key={s.submission_id}
                    to={`/festival/films/${s.submission_id}`}
                    className="block group"
                  >
                    {s.poster_url ? (
                      <img src={s.poster_url} alt={s.title}
                           className="aspect-[2/3] w-full object-cover rounded-md group-hover:scale-[1.02] transition" />
                    ) : (
                      <div className="aspect-[2/3] w-full rounded-md bg-gradient-to-br from-amber-900/40 to-black flex items-center justify-center">
                        <Award className="w-8 h-8 text-amber-500/40" />
                      </div>
                    )}
                    <div className="text-sm font-semibold mt-2 line-clamp-2">{s.title}</div>
                    <div className="text-xs text-gray-500">{s.duration_min} min · {s.region}</div>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* Videos (content) */}
          {p.videos.length > 0 && (
            <section data-testid="creator-videos-section">
              <h2 className="text-xl md:text-2xl font-bold mb-4 flex items-center gap-2">
                <Film className="w-5 h-5 text-violet-400" /> Films & series
                <span className="text-xs text-gray-500 font-normal">· {p.videos.length}</span>
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {p.videos.map((v) => (
                  <Link key={v.id} to={`/title/${v.id}`} className="block group">
                    {v.poster ? (
                      <img src={v.poster} alt={v.title}
                           className="aspect-[2/3] w-full object-cover rounded-md group-hover:scale-[1.02] transition" />
                    ) : (
                      <div className="aspect-[2/3] w-full rounded-md bg-gradient-to-br from-violet-900/40 to-black flex items-center justify-center">
                        <Film className="w-8 h-8 text-violet-500/40" />
                      </div>
                    )}
                    <div className="text-sm font-semibold mt-2 line-clamp-2">{v.title}</div>
                    <div className="text-xs text-gray-500">{v.year} · {v.type}</div>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* Empty state */}
          {p.channels.length === 0 && p.videos.length === 0 && p.submissions.length === 0 && (
            <section data-testid="creator-empty" className="rounded-xl border border-dashed border-white/10 bg-[#0a0a0a] px-8 py-16 text-center max-w-xl mx-auto">
              <Sparkles className="w-10 h-10 mx-auto text-[#f6c34a]/50 mb-4" />
              <h3 className="text-xl font-bold mb-2">Nothing published yet</h3>
              <p className="text-gray-500 text-sm">{p.name} hasn't released a film, scheduled a channel, or submitted to the festival yet. Follow them to be the first to know when they do.</p>
            </section>
          )}
        </div>

        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 mt-12">
          <Link to="/browse" className="text-sm text-gray-500 hover:text-white inline-flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Back to Browse
          </Link>
        </div>

        {shareOpen && (
          <div
            className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
            onClick={() => setShareOpen(false)}
            data-testid="creator-share-modal"
          >
            <div
              onClick={(e) => e.stopPropagation()}
              className="bg-[#0f0f0f] border border-white/10 rounded-xl p-6 w-full max-w-lg"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold flex items-center gap-2">
                  <Share2 className="w-5 h-5 text-[#f6c34a]" /> Share this profile
                </h3>
                <button onClick={() => setShareOpen(false)} className="text-gray-400 hover:text-white">✕</button>
              </div>
              <img
                src={ogUrl}
                alt={`Share card for ${p.name}`}
                className="w-full rounded-lg border border-white/10 mb-4"
                data-testid="creator-share-og-preview"
              />
              <p className="text-xs text-gray-400 mb-4">
                This card appears automatically when you paste the link in Twitter, Slack, Discord, or iMessage.
              </p>
              <div className="flex gap-2">
                <input
                  readOnly
                  value={shareUrl}
                  className="input-dark flex-1 text-xs"
                  onClick={(e) => e.target.select()}
                  data-testid="creator-share-url-input"
                />
                <button
                  data-testid="creator-share-copy-btn"
                  onClick={copyShare}
                  className="btn-red px-4 py-2 rounded text-sm font-semibold flex items-center gap-1.5 shrink-0"
                >
                  {copied ? <><CheckIcon className="w-4 h-4" /> Copied</> : <><Copy className="w-4 h-4" /> Copy</>}
                </button>
              </div>
              <div className="flex gap-2 mt-3 text-xs">
                <a
                  href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(`Check out ${p.name} on FK Cinema`)}`}
                  target="_blank" rel="noreferrer"
                  className="flex-1 text-center bg-white/5 hover:bg-white/10 border border-white/10 rounded py-2 transition"
                >Share on Twitter</a>
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(`Check out ${p.name} on FK Cinema — ${shareUrl}`)}`}
                  target="_blank" rel="noreferrer"
                  className="flex-1 text-center bg-white/5 hover:bg-white/10 border border-white/10 rounded py-2 transition"
                >WhatsApp</a>
              </div>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
